import { useState, useRef, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useWorkspace } from './WorkspaceContext';
import styles from './WorkspaceSwitcher.module.css';

export function WorkspaceSwitcher() {
  const { memberships, activeWorkspace, isLoading, setActiveWorkspaceId } = useWorkspace();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (isLoading || activeWorkspace?.role !== 'admin') return null;

  const workspaceName = activeWorkspace?.workspace.name ?? null;
  const initials = workspaceName ? workspaceName.charAt(0).toUpperCase() : '+';

  return (
    <div className={styles.root} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Switch workspace"
      >
        <div className={styles.avatar} aria-hidden>
          {initials}
        </div>
        <div className={styles.info}>
          {workspaceName ? (
            <>
              <span className={styles.label}>{workspaceName}</span>
              <span className={styles.sub}>Workspace</span>
            </>
          ) : (
            <span className={styles.label}>No workspace</span>
          )}
        </div>
        <svg
          className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </button>

      {open && (
        <div className={styles.menu}>
          {memberships.length === 0 ? (
            <div className={styles.empty}>No workspaces yet</div>
          ) : (
            memberships.map((m) => {
              const active = m.workspace.id === activeWorkspace?.workspace.id;
              return (
                <button
                  key={m.workspace.id}
                  type="button"
                  className={`${styles.item} ${active ? styles.itemActive : ''}`}
                  onClick={() => {
                    setActiveWorkspaceId(m.workspace.id);
                    setOpen(false);
                  }}
                >
                  <div className={`${styles.itemAvatar} ${active ? styles.itemAvatarActive : ''}`}>
                    {m.workspace.name.charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.itemInfo}>
                    <span className={styles.itemName}>{m.workspace.name}</span>
                    <span className={styles.itemRole}>{m.role}</span>
                  </div>
                  {active && (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M2.5 7l3.5 3.5 5.5-6" />
                    </svg>
                  )}
                </button>
              );
            })
          )}
          <div className={styles.divider} />
          <Link to="/workspaces/new" className={styles.newLink} onClick={() => setOpen(false)}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
              <path d="M7 2v10M2 7h10" />
            </svg>
            New workspace
          </Link>
        </div>
      )}
    </div>
  );
}
